import React, { useCallback, useEffect, useRef, useState } from 'react';
import heic2any from 'heic2any';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ImageIcon from '@mui/icons-material/Image';
import api from '../api';

const CATEGORIES = [
  { value: 'gas_fuel', label: 'Gas / Fuel' },
  { value: 'feed',     label: 'Feed' },
  { value: 'hay',      label: 'Hay' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'vet',      label: 'Veterinary' },
  { value: 'misc',     label: 'Miscellaneous' },
];

const SPLIT_TYPES = [
  { value: 'proportional', label: 'Proportional', desc: 'By cow-share weight' },
  { value: 'even',         label: 'Even',         desc: '⅓ each' },
  { value: 'single',       label: 'Single',       desc: 'One farmer only' },
  { value: 'custom',       label: 'Custom',       desc: 'Set % manually' },
];

function formatSaveError(data) {
  if (!data) return 'Failed to save receipt.';
  const parts = [];
  for (const [key, val] of Object.entries(data)) {
    if (Array.isArray(val) && val.length > 0 && val[0] && typeof val[0] === 'object') {
      val.forEach((itemErrors, i) => {
        if (!itemErrors || !Object.keys(itemErrors).length) return;
        Object.entries(itemErrors).forEach(([f, e]) => {
          parts.push(`Line item ${i + 1} — ${f}: ${Array.isArray(e) ? e.join(', ') : e}`);
        });
      });
    } else {
      parts.push(`${key}: ${Array.isArray(val) ? val.join(', ') : val}`);
    }
  }
  return parts.join(' | ') || 'Save failed.';
}

const CATEGORY_COLORS = {
  gas_fuel: 'default', feed: 'success', hay: 'warning',
  equipment: 'secondary', vet: 'error', misc: 'default',
};

async function convertToJpeg(file) {
  const isHeic = file.type === 'image/heic' || file.type === 'image/heif'
    || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
  if (!isHeic) return file;
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
  return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
}

// ── Receipt image gallery ──────────────────────────────────────────────────────

function ReceiptImageDialog({ images, open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [displayUrls, setDisplayUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !images?.length) return;
    setDisplayUrls([]);
    setError('');
    setLoading(true);

    Promise.all(images.map(async (url) => {
      if (/\.(heic|heif)$/i.test(url)) {
        const r = await fetch(url);
        const blob = await r.blob();
        const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.85 });
        return URL.createObjectURL(converted);
      }
      return url;
    }))
      .then(setDisplayUrls)
      .catch(() => setError('Could not load one or more images.'))
      .finally(() => setLoading(false));
  }, [open, images]);

  useEffect(() => {
    return () => {
      displayUrls.forEach((url) => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
    };
  }, [displayUrls]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        {images?.length > 1 ? `Receipt Images (${images.length})` : 'Receipt Image'}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', minHeight: 200 }}>
        {loading && <CircularProgress sx={{ mt: 4 }} />}
        {error && <Alert severity="error">{error}</Alert>}
        {displayUrls.map((url, i) => (
          <Box key={i} mb={displayUrls.length > 1 ? 2 : 0}>
            {displayUrls.length > 1 && (
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {i + 1} / {displayUrls.length}
              </Typography>
            )}
            <img
              src={url}
              alt={`Receipt ${i + 1} of ${displayUrls.length}`}
              style={{ maxWidth: '100%', maxHeight: isMobile ? '60vh' : '75vh' }}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Extra photo thumbnails ─────────────────────────────────────────────────────

function PhotoStrip({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
      {files.map(({ url }, i) => (
        <Box key={i} position="relative" sx={{ width: 64, height: 64, flexShrink: 0 }}>
          <img src={url} alt="Pending upload preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
          <IconButton
            size="small"
            aria-label="Remove photo"
            onClick={() => onRemove(i)}
            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', p: '2px', border: '1px solid', borderColor: 'divider' }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

// ── Split preview ──────────────────────────────────────────────────────────────

function AllocationPreview({ allocations }) {
  if (!allocations?.length) return null;
  return (
    <Box mt={2}>
      <Typography variant="caption" color="text.secondary" textTransform="uppercase">Split Preview</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Farmer</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allocations.map((a) => (
              <TableRow key={a.farmer_id}>
                <TableCell>{a.farmer_name}</TableCell>
                <TableCell align="right">{a.percent}%</TableCell>
                <TableCell align="right">${a.amount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Delete confirmation dialog ─────────────────────────────────────────────────

function DeleteReceiptDialog({ receipt, open, onClose, onDeleted }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/receipts/${receipt.id}/`);
      await onDeleted();
      onClose();
    } catch {
      setError('Failed to delete receipt.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>Delete Receipt?</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography>
          Delete <strong>{receipt?.description}</strong> (${parseFloat(receipt?.amount || 0).toFixed(2)}) from {receipt?.date}?
          This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}>
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Edit receipt dialog ────────────────────────────────────────────────────────

function EditReceiptDialog({ receipt, open, onClose, onSaved, farmers }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState({});
  const [selectedFarmers, setSelectedFarmers] = useState([]);
  const [customPcts, setCustomPcts] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Extra photos
  const [extraImages, setExtraImages] = useState([]);   // existing ReceiptImage objects
  const [pendingFiles, setPendingFiles] = useState([]); // { file, url } pending upload
  const originalImagesRef = useRef([]);
  const extraFilesInputRef = useRef(null);

  const regularFarmers = farmers.filter((f) => !f.is_farm);

  useEffect(() => {
    if (!receipt) return;
    setForm({
      date: receipt.date ?? '',
      amount: receipt.amount ?? '',
      description: receipt.description ?? '',
      category: receipt.category ?? 'misc',
      paid_by: receipt.paid_by ?? '',
      split_type: receipt.split_type ?? 'proportional',
      receipt_number: receipt.receipt_number ?? '',
      qty: receipt.qty ?? '',
    });
    setLineItems((receipt.line_items || []).map((li) => ({
      description: li.description,
      qty: li.qty ?? '',
      unit_price: li.unit_price ?? '',
      amount: li.amount ?? '',
      category: li.category ?? '',
    })));
    const allocationFarmerIds = (receipt.allocations || []).map((a) => String(a.farmer));
    setSelectedFarmers(allocationFarmerIds);
    if (receipt.split_type === 'custom') {
      const pcts = {};
      (receipt.allocations || []).forEach((a) => { pcts[String(a.farmer)] = a.percent; });
      setCustomPcts(pcts);
    } else {
      setCustomPcts({});
    }
    const imgs = receipt.extra_images || [];
    setExtraImages(imgs);
    originalImagesRef.current = imgs;
    setPendingFiles((prev) => { prev.forEach((f) => URL.revokeObjectURL(f.url)); return []; });
    setError('');
  }, [receipt]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const addLineItem = () => setLineItems((prev) => [...prev, { description: '', qty: '', unit_price: '', amount: '', category: '' }]);
  const removeLineItem = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, value) => {
    setLineItems((prev) => prev.map((li, i) => {
      if (i !== idx) return li;
      const next = { ...li, [field]: value };
      if (field === 'qty' || field === 'unit_price') {
        const q = parseFloat(field === 'qty' ? value : next.qty);
        const p = parseFloat(field === 'unit_price' ? value : next.unit_price);
        if (!isNaN(q) && !isNaN(p)) next.amount = (q * p).toFixed(2);
      }
      return next;
    }));
  };

  const handleExtraImages = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    const converted = await Promise.all(files.map(async (f) => {
      const file = await convertToJpeg(f);
      return { file, url: URL.createObjectURL(file) };
    }));
    setPendingFiles((prev) => [...prev, ...converted]);
  };

  const removePending = (i) => {
    setPendingFiles((prev) => {
      URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, j) => j !== i);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { ...form, paid_by: Number(form.paid_by) };
      if (!body.receipt_number) body.receipt_number = null;
      body.qty = body.qty !== '' ? body.qty : null;
      const validLineItems = lineItems.filter((li) => li.description.trim() && li.amount);
      body.line_items = validLineItems.map((li) => ({
        description: li.description.trim(),
        qty: li.qty !== '' ? li.qty : null,
        unit_price: li.unit_price !== '' ? li.unit_price : null,
        amount: li.amount,
        category: li.category || '',
      }));
      if (form.split_type === 'single') body.farmer_ids = [Number(selectedFarmers[0])];
      else if (form.split_type === 'custom') {
        body.custom_allocations = Object.entries(customPcts).map(([fid, pct]) => ({
          farmer_id: Number(fid), percent: pct,
        }));
      } else if (selectedFarmers.length > 0) {
        body.farmer_ids = selectedFarmers.map(Number);
      }
      await api.patch(`/receipts/${receipt.id}/`, body);

      // Delete extra images that were removed
      const toDelete = originalImagesRef.current.filter(
        (img) => !extraImages.some((e) => e.id === img.id)
      );
      await Promise.all(toDelete.map((img) => api.delete(`/receipt-images/${img.id}/`)));

      // Upload new extra images
      for (const { file } of pendingFiles) {
        const fd = new FormData();
        fd.append('receipt', receipt.id);
        fd.append('image', file);
        await api.post('/receipt-images/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      await onSaved();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      console.error('Receipt save error:', data);
      setError(formatSaveError(data));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Edit Receipt</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField label="Date" type="date" value={form.date ?? ''} onChange={set('date')} required fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Amount ($)" type="number" value={form.amount ?? ''} onChange={set('amount')} required fullWidth inputProps={{ step: '0.01', min: '0' }} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Description" value={form.description ?? ''} onChange={set('description')} required fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Receipt #" value={form.receipt_number ?? ''} onChange={set('receipt_number')} fullWidth />
          </Grid>
        </Grid>

        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <TextField select label="Category" value={form.category ?? ''} onChange={set('category')} required fullWidth>
            {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Qty"
            type="number"
            value={form.qty ?? ''}
            onChange={set('qty')}
            fullWidth
            inputProps={{ step: '0.001', min: '0' }}
          />
          <TextField select label="Paid By" value={form.paid_by ?? ''} onChange={set('paid_by')} required fullWidth>
            {regularFarmers.map((f) => <MenuItem key={f.id} value={f.id}>{f.username}</MenuItem>)}
          </TextField>
        </Box>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Divider />
            <Typography variant="caption" color="text.secondary" textTransform="uppercase" display="block" mt={1} mb={1}>
              Split Type
            </Typography>
            <ToggleButtonGroup
              value={form.split_type ?? 'proportional'}
              exclusive
              onChange={(_, v) => { if (v) setForm((prev) => ({ ...prev, split_type: v })); }}
              size="small"
              fullWidth
            >
              {SPLIT_TYPES.map((s) => (
                <ToggleButton key={s.value} value={s.value} sx={{ flexDirection: 'column', py: 1 }}>
                  <Typography variant="caption" fontWeight={700}>{s.label}</Typography>
                  <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>{s.desc}</Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          {form.split_type && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {form.split_type === 'single' ? 'Charge to:' : form.split_type === 'custom' ? 'Farmers included:' : 'Limit to (optional):'}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {regularFarmers.map((f) => (
                  <Chip key={f.id} label={f.username} clickable
                    color={selectedFarmers.includes(String(f.id)) ? 'primary' : 'default'}
                    onClick={() => {
                      const sid = String(f.id);
                      if (form.split_type === 'single') {
                        setSelectedFarmers([sid]);
                      } else {
                        setSelectedFarmers((prev) =>
                          prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
                        );
                      }
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {form.split_type === 'custom' && selectedFarmers.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {selectedFarmers.map((fid) => {
                  const farmer = regularFarmers.find((f) => String(f.id) === fid);
                  return (
                    <Grid item xs={6} sm={4} key={fid}>
                      <TextField label={`${farmer?.username} %`} type="number" size="small"
                        value={customPcts[fid] ?? ''}
                        onChange={(e) => setCustomPcts({ ...customPcts, [fid]: e.target.value })}
                        inputProps={{ min: 0, max: 100, step: '0.01' }} fullWidth />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}

          {/* Line items */}
          <Grid item xs={12}>
            <Divider />
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} mb={1}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                Line Items (optional)
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addLineItem}>Add Item</Button>
            </Box>
            {lineItems.map((li, idx) => (
              <Box key={idx} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 0.5 }}>
                <Box display="flex" gap={1} alignItems="center" mb={0.5}>
                  <TextField placeholder="Description" size="small" value={li.description}
                    onChange={(e) => updateLineItem(idx, 'description', e.target.value)} sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={() => removeLineItem(idx)} color="error" aria-label="Remove line item">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <TextField select size="small" value={li.category}
                      onChange={(e) => updateLineItem(idx, 'category', e.target.value)} fullWidth
                      SelectProps={{ displayEmpty: true }}
                      inputProps={{ 'aria-label': 'Category' }}>
                      <MenuItem value=""><em>Same as receipt</em></MenuItem>
                      {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField placeholder="Qty" size="small" type="number" value={li.qty}
                      onChange={(e) => updateLineItem(idx, 'qty', e.target.value)} fullWidth inputProps={{ min: 0, step: 'any' }} />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField placeholder="Unit $" size="small" type="number" value={li.unit_price}
                      onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} fullWidth inputProps={{ min: 0, step: '0.01' }} />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField placeholder="Amount" size="small" type="number" value={li.amount}
                      onChange={(e) => updateLineItem(idx, 'amount', e.target.value)} fullWidth inputProps={{ min: 0, step: '0.01' }} />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Grid>

          {/* Additional photos */}
          <Grid item xs={12}>
            <Divider />
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} mb={1}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                Additional Photos
              </Typography>
              <Button size="small" startIcon={<AddPhotoAlternateIcon />} onClick={() => extraFilesInputRef.current.click()}>
                Add Photos
              </Button>
            </Box>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
              multiple
              style={{ display: 'none' }}
              ref={extraFilesInputRef}
              onChange={handleExtraImages}
            />
            <Box display="flex" gap={1} flexWrap="wrap">
              {extraImages.map((img) => (
                <Box key={img.id} position="relative" sx={{ width: 64, height: 64, flexShrink: 0 }}>
                  <img src={img.image} alt="Saved receipt attachment" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove photo"
                    onClick={() => setExtraImages((prev) => prev.filter((e) => e.id !== img.id))}
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', p: '2px', border: '1px solid', borderColor: 'divider' }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              <PhotoStrip files={pendingFiles} onRemove={removePending} />
            </Box>
            {extraImages.length === 0 && pendingFiles.length === 0 && (
              <Typography variant="caption" color="text.disabled">No additional photos</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}
          disabled={saving || !form.paid_by || !form.date || !form.amount}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Add receipt dialog ─────────────────────────────────────────────────────────

function AddReceiptDialog({ open, onClose, onSaved, farmers }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const emptyForm = {
    date: '', amount: '', description: '', category: 'misc',
    paid_by: '', split_type: 'proportional', receipt_number: '', qty: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [selectedFarmers, setSelectedFarmers] = useState([]);
  const [customPcts, setCustomPcts] = useState({});
  const [preview, setPreview] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [extraFiles, setExtraFiles] = useState([]); // { file, url }

  const fileInputRef = useRef(null);
  const extraFilesInputRef = useRef(null);

  // Reset all state whenever the dialog opens so stale data never carries over
  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setLineItems([]);
      setSelectedFarmers([]);
      setCustomPcts({});
      setPreview([]);
      setImageFile(null);
      setError('');
      setExtractError('');
      setExtraFiles((prev) => { prev.forEach((f) => URL.revokeObjectURL(f.url)); return []; });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addLineItem = () => setLineItems((prev) => [...prev, { description: '', qty: '', unit_price: '', amount: '', category: '' }]);
  const removeLineItem = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, value) => {
    setLineItems((prev) => prev.map((li, i) => {
      if (i !== idx) return li;
      const next = { ...li, [field]: value };
      if (field === 'qty' || field === 'unit_price') {
        const q = parseFloat(field === 'qty' ? value : next.qty);
        const p = parseFloat(field === 'unit_price' ? value : next.unit_price);
        if (!isNaN(q) && !isNaN(p)) next.amount = (q * p).toFixed(2);
      }
      return next;
    }));
  };

  const regularFarmers = farmers.filter((f) => !f.is_farm);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleImageSelect = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    setExtractError('');
    setExtracting(true);
    try {
      file = await convertToJpeg(file);
    } catch {
      setExtractError('Could not convert HEIC image. Try saving as JPEG first.');
      setExtracting(false);
      return;
    }
    setImageFile(file);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/receipts/extract/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { date, amount, description, category, receipt_number } = res.data;
      const filenameNumber = file.name.replace(/\.[^.]+$/, '');
      setForm((prev) => ({
        ...prev,
        ...(date ? { date } : {}),
        ...(amount != null ? { amount: String(amount) } : {}),
        ...(description ? { description } : {}),
        ...(category ? { category } : {}),
        receipt_number: receipt_number || filenameNumber,
      }));
    } catch (err) {
      setExtractError(err.response?.data?.error || 'Extraction failed. Fill in fields manually.');
    } finally {
      setExtracting(false);
    }
  };

  const handleExtraImages = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    const converted = await Promise.all(files.map(async (f) => {
      const file = await convertToJpeg(f);
      return { file, url: URL.createObjectURL(file) };
    }));
    setExtraFiles((prev) => [...prev, ...converted]);
  };

  const removeExtraFile = (i) => {
    setExtraFiles((prev) => {
      URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, j) => j !== i);
    });
  };

  const handlePreview = async () => {
    if (!form.date || !form.amount) return;
    setPreviewing(true);
    try {
      const body = { date: form.date, amount: form.amount, split_type: form.split_type };
      if (form.split_type === 'single') body.farmer_ids = [Number(selectedFarmers[0])];
      else if (form.split_type === 'custom') {
        body.custom_allocations = Object.entries(customPcts).map(([fid, pct]) => ({
          farmer_id: Number(fid), percent: pct,
        }));
      } else if (selectedFarmers.length > 0) {
        body.farmer_ids = selectedFarmers.map(Number);
      }
      const res = await api.post('/receipts/preview_split/', body);
      setPreview(res.data.allocations);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { ...form, paid_by: Number(form.paid_by) };
      if (!body.receipt_number) body.receipt_number = null;
      body.qty = body.qty !== '' ? body.qty : null;
      const validLineItems = lineItems.filter((li) => li.description.trim() && li.amount);
      if (validLineItems.length > 0) {
        body.line_items = validLineItems.map((li) => ({
          description: li.description.trim(),
          qty: li.qty !== '' ? li.qty : null,
          unit_price: li.unit_price !== '' ? li.unit_price : null,
          amount: li.amount,
          category: li.category || '',
        }));
      }
      if (form.split_type === 'single') body.farmer_ids = [Number(selectedFarmers[0])];
      else if (form.split_type === 'custom') {
        body.custom_allocations = Object.entries(customPcts).map(([fid, pct]) => ({
          farmer_id: Number(fid), percent: pct,
        }));
      } else if (selectedFarmers.length > 0) {
        body.farmer_ids = selectedFarmers.map(Number);
      }
      const res = await api.post('/receipts/', body);

      // Upload scan image
      if (imageFile) {
        try {
          const imgData = new FormData();
          imgData.append('receipt_image', imageFile);
          await api.patch(`/receipts/${res.data.id}/`, imgData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          // non-fatal
        }
      }

      // Upload extra images
      for (const { file } of extraFiles) {
        try {
          const fd = new FormData();
          fd.append('receipt', res.data.id);
          fd.append('image', file);
          await api.post('/receipt-images/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch {
          // non-fatal
        }
      }

      await onSaved();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      console.error('Receipt save error:', data);
      setError(formatSaveError(data));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Add Receipt</DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleImageSelect}
        />
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
          multiple
          style={{ display: 'none' }}
          ref={extraFilesInputRef}
          onChange={handleExtraImages}
        />

        <Box display="flex" alignItems="flex-start" gap={2} mb={2} flexWrap="wrap">
          <Box>
            <Button
              variant="outlined"
              startIcon={extracting ? <CircularProgress size={16} /> : <DocumentScannerIcon />}
              onClick={() => fileInputRef.current.click()}
              disabled={extracting}
            >
              {extracting ? 'Scanning…' : 'Scan Receipt Photo'}
            </Button>
            {imageFile && !extracting && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} noWrap sx={{ maxWidth: 200 }}>
                {imageFile.name}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => extraFilesInputRef.current.click()}
          >
            Add Photos
          </Button>
        </Box>
        {extractError && <Alert severity="warning" sx={{ mb: 2 }}>{extractError}</Alert>}
        <PhotoStrip files={extraFiles} onRemove={removeExtraFile} />

        <Grid container spacing={2} sx={{ pt: extraFiles.length ? 2 : 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField label="Date" type="date" value={form.date} onChange={set('date')} required fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Amount ($)" type="number" value={form.amount} onChange={set('amount')} required fullWidth inputProps={{ step: '0.01', min: '0' }} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Description" value={form.description} onChange={set('description')} required fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Receipt #" value={form.receipt_number} onChange={set('receipt_number')} fullWidth />
          </Grid>
        </Grid>

        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <TextField select label="Category" value={form.category} onChange={set('category')} required fullWidth>
            {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Qty"
            type="number"
            value={form.qty}
            onChange={set('qty')}
            fullWidth
            inputProps={{ step: '0.001', min: '0' }}
          />
          <TextField select label="Paid By" value={form.paid_by} onChange={set('paid_by')} required fullWidth>
            {regularFarmers.map((f) => <MenuItem key={f.id} value={f.id}>{f.username}</MenuItem>)}
          </TextField>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Divider />
            <Typography variant="caption" color="text.secondary" textTransform="uppercase" display="block" mt={1} mb={1}>
              Split Type
            </Typography>
            <ToggleButtonGroup
              value={form.split_type}
              exclusive
              onChange={(_, v) => { if (v) setForm({ ...form, split_type: v }); setPreview([]); }}
              size="small"
              fullWidth
            >
              {SPLIT_TYPES.map((s) => (
                <ToggleButton key={s.value} value={s.value} sx={{ flexDirection: 'column', py: 1 }}>
                  <Typography variant="caption" fontWeight={700}>{s.label}</Typography>
                  <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>{s.desc}</Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          {(form.split_type === 'single' || form.split_type === 'custom' || form.split_type === 'proportional' || form.split_type === 'even') && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {form.split_type === 'single' ? 'Charge to:' : form.split_type === 'custom' ? 'Farmers included:' : 'Limit to (optional — leave blank for all):'}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {regularFarmers.map((f) => (
                  <Chip
                    key={f.id}
                    label={f.username}
                    clickable
                    color={selectedFarmers.includes(String(f.id)) ? 'primary' : 'default'}
                    onClick={() => {
                      const sid = String(f.id);
                      if (form.split_type === 'single') {
                        setSelectedFarmers([sid]);
                      } else {
                        setSelectedFarmers((prev) =>
                          prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
                        );
                      }
                      setPreview([]);
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {form.split_type === 'custom' && selectedFarmers.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {selectedFarmers.map((fid) => {
                  const farmer = regularFarmers.find((f) => String(f.id) === fid);
                  return (
                    <Grid item xs={6} sm={4} key={fid}>
                      <TextField
                        label={`${farmer?.username} %`}
                        type="number"
                        size="small"
                        value={customPcts[fid] ?? ''}
                        onChange={(e) => setCustomPcts({ ...customPcts, [fid]: e.target.value })}
                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                        fullWidth
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePreview}
              disabled={!form.date || !form.amount || previewing}
              startIcon={previewing ? <CircularProgress size={14} /> : null}
            >
              Preview Split
            </Button>
            <AllocationPreview allocations={preview} />
          </Grid>

          {/* Line items */}
          <Grid item xs={12}>
            <Divider />
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} mb={1}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                Line Items (optional)
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addLineItem}>Add Item</Button>
            </Box>
            {lineItems.length > 0 && (
              <>
                {lineItems.map((li, idx) => (
                  <Box key={idx} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 0.5 }}>
                    <Box display="flex" gap={1} alignItems="center" mb={0.5}>
                      <TextField placeholder="Description" size="small" value={li.description}
                        onChange={(e) => updateLineItem(idx, 'description', e.target.value)} sx={{ flex: 1 }} />
                      <IconButton size="small" onClick={() => removeLineItem(idx)} color="error" aria-label="Remove line item">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <TextField select size="small" value={li.category}
                          onChange={(e) => updateLineItem(idx, 'category', e.target.value)} fullWidth
                          SelectProps={{ displayEmpty: true }}
                          inputProps={{ 'aria-label': 'Category' }}>
                          <MenuItem value=""><em>Same as receipt</em></MenuItem>
                          {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <TextField placeholder="Qty" size="small" type="number" value={li.qty}
                          onChange={(e) => updateLineItem(idx, 'qty', e.target.value)} fullWidth inputProps={{ min: 0, step: 'any' }} />
                      </Grid>
                      <Grid item xs={4} sm={3}>
                        <TextField placeholder="Unit $" size="small" type="number" value={li.unit_price}
                          onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} fullWidth inputProps={{ min: 0, step: '0.01' }} />
                      </Grid>
                      <Grid item xs={4} sm={3}>
                        <TextField placeholder="Amount" size="small" type="number" value={li.amount}
                          onChange={(e) => updateLineItem(idx, 'amount', e.target.value)} fullWidth inputProps={{ min: 0, step: '0.01' }} />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                {(() => {
                  const liTotal = lineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
                  const receiptTotal = parseFloat(form.amount) || 0;
                  const mismatch = receiptTotal > 0 && Math.abs(liTotal - receiptTotal) > 0.02;
                  return (
                    <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Items total: ${liTotal.toFixed(2)}
                      </Typography>
                      {mismatch && (
                        <Chip size="small" color="warning" label={`≠ receipt $${receiptTotal.toFixed(2)}`} />
                      )}
                    </Box>
                  );
                })()}
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.paid_by || !form.date || !form.amount}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save Receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function Receipts() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [receipts, setReceipts] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, receipt: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, receipt: null });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [imageDialog, setImageDialog] = useState({ open: false, images: [] });

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/receipts/', { params: { _t: Date.now() } });
      setReceipts(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get('/farmers/').then((res) => setFarmers(res.data));
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Category filter — uses per-line-item category when set, falls back to receipt category.
  // Receipts with no line items are represented as a single entry.
  const filteredReceipts = categoryFilter
    ? receipts.filter((r) => r.category === categoryFilter)
    : receipts;

  const lineItems = categoryFilter
    ? receipts.flatMap((r) => {
        if ((r.line_items || []).length > 0) {
          return r.line_items
            .filter((li) => (li.category || r.category) === categoryFilter)
            .map((li) => ({
              ...li,
              effective_category: li.category || r.category,
              date: r.date,
              receipt_number: r.receipt_number,
              paid_by_name: r.paid_by_name,
              receipt_image: r.receipt_image,
            }));
        }
        if (r.category === categoryFilter) {
          return [{ id: `r-${r.id}`, description: r.description, amount: r.amount,
            qty: r.qty ?? null, unit_price: null, effective_category: r.category,
            date: r.date, receipt_number: r.receipt_number,
            paid_by_name: r.paid_by_name, receipt_image: r.receipt_image }];
        }
        return [];
      })
    : [];

  const total = categoryFilter
    ? lineItems.reduce((sum, li) => sum + parseFloat(li.amount || 0), 0)
    : receipts.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);

  const openImages = (images) => setImageDialog({ open: true, images });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>Receipts</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Receipt
        </Button>
      </Box>

      {/* Category filter */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Chip
          label="All"
          clickable
          color={!categoryFilter ? 'primary' : 'default'}
          onClick={() => setCategoryFilter('')}
        />
        {CATEGORIES.map((c) => (
          <Chip
            key={c.value}
            label={c.label}
            clickable
            color={categoryFilter === c.value ? 'primary' : 'default'}
            onClick={() => setCategoryFilter(c.value)}
          />
        ))}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <>
          {/* ── All tab: receipt-level view ── */}
          {!categoryFilter && (
            isMobile ? (
              /* Mobile: stacked cards */
              filteredReceipts.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>No receipts yet.</Typography>
              ) : (
                <Stack spacing={1.5}>
                  {filteredReceipts.map((r) => {
                    const allImages = [r.receipt_image, ...(r.extra_images || []).map((i) => i.image)].filter(Boolean);
                    return (
                      <Card key={r.id} variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <Typography variant="subtitle2" fontWeight={700}>{r.description}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {r.date}{r.receipt_number ? ` · #${r.receipt_number}` : ''}
                              </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>${parseFloat(r.amount).toFixed(2)}</Typography>
                          </Box>
                          <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                            <Chip size="small" label={CATEGORIES.find((c) => c.value === r.category)?.label ?? r.category} color={CATEGORY_COLORS[r.category]} />
                            <Chip size="small" variant="outlined" label={r.split_type} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" mb={0.5}>
                            Paid by {r.paid_by_name}
                          </Typography>
                          <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                            {r.allocations?.map((a) => (
                              <Chip key={a.farmer} size="small" label={`${a.farmer_name} $${parseFloat(a.amount).toFixed(2)}`} variant="outlined" />
                            ))}
                          </Box>
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                            {allImages.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => openImages(allImages)}
                                aria-label={`View ${allImages.length} receipt image${allImages.length > 1 ? 's' : ''}`}
                              >
                                <ImageIcon fontSize="small" />
                                {allImages.length > 1 && (
                                  <Typography variant="caption" sx={{ fontSize: 10, ml: 0.25 }}>{allImages.length}</Typography>
                                )}
                              </IconButton>
                            )}
                            <IconButton size="small" onClick={() => setEditDialog({ open: true, receipt: r })} aria-label="Edit receipt">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, receipt: r })} aria-label="Delete receipt">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )
            ) : (
              /* Desktop: table */
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Receipt #</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Category</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Paid By</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Split</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Allocations</TableCell>
                      <TableCell scope="col" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReceipts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No receipts yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredReceipts.map((r) => {
                        const allImages = [r.receipt_image, ...(r.extra_images || []).map((i) => i.image)].filter(Boolean);
                        return (
                          <TableRow key={r.id} hover>
                            <TableCell>{r.date}</TableCell>
                            <TableCell sx={{ color: r.receipt_number ? 'inherit' : 'text.disabled', fontStyle: r.receipt_number ? 'normal' : 'italic' }}>
                              {r.receipt_number || '—'}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={CATEGORIES.find((c) => c.value === r.category)?.label ?? r.category} color={CATEGORY_COLORS[r.category]} />
                            </TableCell>
                            <TableCell>{r.paid_by_name}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>${parseFloat(r.amount).toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip size="small" variant="outlined" label={r.split_type} />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {r.allocations?.map((a) => (
                                  <Chip
                                    key={a.farmer}
                                    size="small"
                                    label={`${a.farmer_name} $${parseFloat(a.amount).toFixed(2)}`}
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ p: 0.5, whiteSpace: 'nowrap' }}>
                              {allImages.length > 0 && (
                                <Tooltip title={allImages.length > 1 ? `View ${allImages.length} images` : 'View image'}>
                                  <IconButton size="small" onClick={() => openImages(allImages)} aria-label={`View ${allImages.length} receipt image${allImages.length > 1 ? 's' : ''}`}>
                                    <ImageIcon fontSize="small" />
                                    {allImages.length > 1 && (
                                      <Typography variant="caption" sx={{ fontSize: 10, ml: 0.25 }}>{allImages.length}</Typography>
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => setEditDialog({ open: true, receipt: r })} aria-label="Edit receipt">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, receipt: r })} aria-label="Delete receipt">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}

          {/* ── Category tab: line-item table ── */}
          {categoryFilter && (
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {lineItems.length} line items across {receipts.length} receipts
              </Typography>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Receipt #</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Item</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Category</TableCell>
                      {!isMobile && <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Qty</TableCell>}
                      {!isMobile && <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Unit Price</TableCell>}
                      <TableCell scope="col" sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                      <TableCell scope="col" sx={{ fontWeight: 700 }}>Paid By</TableCell>
                      <TableCell scope="col" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 7 : 9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No line items recorded for this category.
                        </TableCell>
                      </TableRow>
                    ) : (
                      lineItems.map((li, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{li.date}</TableCell>
                          <TableCell sx={{ color: li.receipt_number ? 'inherit' : 'text.disabled', fontStyle: li.receipt_number ? 'normal' : 'italic' }}>
                            {li.receipt_number || '—'}
                          </TableCell>
                          <TableCell>{li.description}</TableCell>
                          <TableCell>
                            <Chip size="small" label={CATEGORIES.find((c) => c.value === li.effective_category)?.label ?? li.effective_category} color={CATEGORY_COLORS[li.effective_category]} />
                          </TableCell>
                          {!isMobile && (
                            <TableCell align="right">
                              {li.qty != null ? parseFloat(li.qty).toLocaleString(undefined, { maximumFractionDigits: 3 }) : '—'}
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell align="right">
                              {li.unit_price != null ? `$${parseFloat(li.unit_price).toFixed(2)}` : '—'}
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            ${parseFloat(li.amount).toFixed(2)}
                          </TableCell>
                          <TableCell>{li.paid_by_name}</TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            {li.receipt_image && (
                              <Tooltip title="View receipt image">
                                <IconButton size="small" onClick={() => openImages([li.receipt_image])} aria-label="View receipt image">
                                  <ImageIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Total: ${total.toFixed(2)}
            </Typography>
          </Box>
        </>
      )}

      <AddReceiptDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchReceipts}
        farmers={farmers}
      />

      <ReceiptImageDialog
        open={imageDialog.open}
        images={imageDialog.images}
        onClose={() => setImageDialog({ open: false, images: [] })}
      />

      <EditReceiptDialog
        open={editDialog.open}
        receipt={editDialog.receipt}
        onClose={() => setEditDialog({ open: false, receipt: null })}
        onSaved={fetchReceipts}
        farmers={farmers}
      />

      <DeleteReceiptDialog
        open={deleteDialog.open}
        receipt={deleteDialog.receipt}
        onClose={() => setDeleteDialog({ open: false, receipt: null })}
        onDeleted={fetchReceipts}
      />
    </Box>
  );
}
